import type { MarketAnalysisResult } from "../tools/market_analysis";
import type { StrategiesSSEEvent } from "./types";

function extractSseFrames(buffer: string) {
  const chunks = buffer.split("\n\n");
  if (chunks.length <= 1) return { frames: [] as string[], rest: buffer };
  const rest = chunks[chunks.length - 1] ?? "";
  const frames = chunks
    .slice(0, -1)
    .map((x) => x.trim())
    .filter(Boolean);
  return { frames, rest };
}

function parseSseDataLines(frameText: string) {
  const lines = frameText.split("\n");
  const dataLines: string[] = [];
  for (const ln of lines) {
    const trimmed = ln.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith("data:")) {
      dataLines.push(trimmed.slice("data:".length).trimStart());
    }
  }
  return dataLines.join("\n");
}

export async function streamStrategiesChat(
  sessionId: string,
  message: string,
  marketSelection: MarketAnalysisResult | undefined,
  onEvent: (event: StrategiesSSEEvent) => void
): Promise<void> {
  let res: Response;
  try {
    res = await fetch("/api/v1/strategies/chat", {
      method: "POST",
      headers: {
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        session_id: sessionId,
        message,
        ...(marketSelection ? { market_selection: marketSelection } : {}),
      }),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    onEvent({ type: "error", error: msg });
    throw err;
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    const msg = body || `${res.status} ${res.statusText}`;
    onEvent({ type: "error", error: msg });
    throw new Error(msg);
  }

  if (!res.body) {
    const msg = "Missing response body.";
    onEvent({ type: "error", error: msg });
    throw new Error(msg);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const extracted = extractSseFrames(buffer);
      buffer = extracted.rest;

      for (const frame of extracted.frames) {
        const data = parseSseDataLines(frame);
        if (!data) continue;

        if (data === "[DONE]") {
          onEvent({ type: "done" });
          try {
            await reader.cancel();
          } catch {
          }
          return;
        }

        try {
          onEvent(JSON.parse(data) as StrategiesSSEEvent);
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Invalid SSE event payload.";
          onEvent({ type: "error", error: msg });
        }
      }
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    onEvent({ type: "error", error: msg });
    try {
      await reader.cancel();
    } catch {
    }
    throw err;
  }

  const final = buffer.trim();
  if (!final) return;

  const data = parseSseDataLines(final);
  if (!data) return;

  if (data === "[DONE]") {
    onEvent({ type: "done" });
    return;
  }

  try {
    onEvent(JSON.parse(data) as StrategiesSSEEvent);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Invalid SSE event payload.";
    onEvent({ type: "error", error: msg });
  }
}
